import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { ctaLinkClasses } from '@workspace/ui/components/dsm/cta-button'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useAdminBlogPostsQuery } from '../../../features/admin/hooks/use-admin-api'

type SearchParams = { page?: number; search?: string; status?: string }
const LIMIT = 20

export const Route = createFileRoute('/admin/blog/')({
  component: Page,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
    status: (search.status as string) || undefined,
  }),
})

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

type BlogItem = {
  id: string
  title: string
  slug: string
  status: string
  tags: string[]
  publishedAt: string | null
  createdAt: string
}

const columns: TableColumn<BlogItem>[] = [
  {
    header: 'ID',
    width: '80px',
    render: (post) => (
      <Link
        to="/admin/blog/$id"
        params={{ id: post.id }}
        className="font-mono text-xs text-primary hover:underline"
      >
        {post.id.slice(0, 8)}
      </Link>
    ),
  },
  {
    header: 'Title',
    render: (post) => <CellText>{post.title}</CellText>,
  },
  {
    header: 'Status',
    width: '100px',
    render: (post) => (
      <Badge variant={post.status === 'PUBLISHED' ? 'default' : 'secondary'} size="sm">
        {post.status === 'PUBLISHED' ? 'Published' : 'Draft'}
      </Badge>
    ),
  },
  {
    header: 'Tags',
    render: (post) => (
      <div className="flex flex-wrap gap-1">
        {post.tags.slice(0, 3).map((tag) => (
          <Badge key={tag} variant="outline" size="sm">
            {tag}
          </Badge>
        ))}
        {post.tags.length > 3 && (
          <span className="text-xs text-muted-foreground">+{post.tags.length - 3}</span>
        )}
      </div>
    ),
  },
  {
    header: 'Published',
    width: '140px',
    render: (post) => <CellText>{post.publishedAt ? formatDate(post.publishedAt) : '—'}</CellText>,
  },
  {
    header: 'Created',
    width: '140px',
    render: (post) => <CellText>{formatDate(post.createdAt)}</CellText>,
  },
]

function Page() {
  const { page = 1, search, status } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const postsQuery = useAdminBlogPostsQuery({
    page,
    limit: LIMIT,
    search: search || undefined,
    status: status || undefined,
  })

  const posts = postsQuery.data?.data?.items ?? []
  const total = postsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SearchInput
            placeholder="Search blog posts..."
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={() =>
              navigate({ search: { page: 1, search: searchInput || undefined, status } })
            }
          />
          <select
            value={status ?? ''}
            onChange={(e) =>
              navigate({
                search: { page: 1, search, status: e.target.value || undefined },
              })
            }
            className="rounded-md border border-border bg-input/30 px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
          </select>
        </div>
        <Link to="/admin/blog/new" className={ctaLinkClasses({ size: 'sm' })}>
          <Plus className="size-4" />
          <span>New post</span>
        </Link>
      </div>

      <DataTable
        columns={columns}
        data={posts}
        isLoading={postsQuery.isLoading}
        keyExtractor={(post) => post.id}
      />

      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => navigate({ search: { page: p, search, status } })}
        />
      )}
    </div>
  )
}
